import { Alert } from '@creditornot/cb-alert';
import { Button, TextButton } from '@creditornot/cb-button';
import { ArrowLeft, Document } from '@creditornot/cb-icons';
import { colors, cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { Spinner } from '@creditornot/cb-progress';
import { Radio } from '@creditornot/cb-radio';
import { Tag } from '@creditornot/cb-tag';
import React, { useCallback } from 'react';
import Dropzone from 'react-dropzone';
import toast from 'react-hot-toast';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { Typography as T, typographyCss } from 'common/typography';
import { localize } from 'common/utils';
import FileMatcherConfirmationPrompt from 'modules/bulk-image-upload/components/FileMatcherConfirmationPrompt';
import { useListingManagerRoutes } from 'modules/shared-menu/hooks/useListingManagerRoutes';
import { useSelectionData } from 'modules/shared-menu/hooks/useSelectionData';
import {
  useBulkImageUploadStatusTracking,
  useBulkImageUploadFileSelectionTracking,
} from 'modules/shared-menu/telemetry';
import { SharedMenu } from 'modules/shared-menu/types';

import StatusTable from './components/StatusTable';
import { UploadInProgressConfirmationPrompt } from './components/UploadInProgressConfirmationPrompt';
import useFileMatcher from './hooks/useFileMatcher';
import useBulkImageUpload from './hooks/useSharedMenuBulkImageUpload';

type Props = {
  sharedMenu: SharedMenu;
};

const Header = styled(T.Heading4)`
  display: flex;
  flex-direction: column;
`;

const Root = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`;

const HeaderContainer = styled.div`
  display: flex;
  padding: 44px 24px 16px;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
  align-items: flex-start;
  flex-direction: column;
  justify-content: flex-end;
  gap: 16px;
`;

const StyledTextButton = styled(TextButton)`
  font-weight: bold;
  margin-block-end: 8px;
`;

const ContentWrapper = styled.div`
  flex: 1;
  overflow: auto;
`;

const Content = styled.div`
  margin: auto;
  margin-block-start: 32px;
  max-width: 800px;
`;

const SpinnerContainer = styled.div`
  display: flex;
  height: 100%;
  justify-content: center;
  align-items: center;
`;

export const MenuNameTag = styled(Tag).attrs({ size: 'small', variant: 'lightBlack' })``;

const RadioGroup = styled.div`
  margin: 8px 0 16px;
`;

const StyledRadio = styled(Radio)`
  & + & {
    margin-inline-start: 24px;
  }
`;

const FileDrop = styled.div<{ isLoading?: boolean }>`
  ${typographyCss.Body3};
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  height: 88px;
  border: 2px dashed ${({ isLoading }) => (isLoading ? colors.wolt100 : cbSemanticCssVars.borderPrimary)};
  border-radius: 8px;
  outline: none;
`;

const FileSelectLink = styled(T.Body3)`
  cursor: pointer;
  font-weight: 500;
  color: ${colors.wolt100};
`;

const FileSpecs = styled.div`
  ${typographyCss.Small};
  margin-block-start: 4px;
  color: ${cbSemanticCssVars.textSecondary};
`;

const UploadButtonContainer = styled.div`
  width: 100%;
  margin-block-start: 24px;
  display: flex;
  justify-content: center;
`;

export const SharedMenuBulkImageUploadPage = ({ sharedMenu }: Props) => {
  const history = useHistory();
  const { menuEditorPage } = useListingManagerRoutes();

  const { offers, loadingInitialSelectionData, loadingSelectionData } = useSelectionData();

  const loadingData = loadingInitialSelectionData || loadingSelectionData;

  const {
    handleFileMatching,
    results: matchingResults,
    matchBy,
    setMatchBy,
    isMatching,
    setErrors,
  } = useFileMatcher(offers);
  const { handleBulkImageUpload, uploadStatus } = useBulkImageUpload(sharedMenu.id, matchingResults || [], setErrors);

  const goBackToMenuEditor = useCallback(() => {
    toast.dismiss();
    history.push(menuEditorPage());
  }, [history, menuEditorPage]);

  const isPending = !!matchingResults?.filter(r => r.status === 'success').length && uploadStatus === 'idle';
  const isUploading = uploadStatus === 'loading';

  useBulkImageUploadStatusTracking(uploadStatus);
  useBulkImageUploadFileSelectionTracking(matchingResults ?? []);

  return (
    <Root>
      <HeaderContainer>
        <Header>
          <StyledTextButton
            variant="neutral"
            size="small"
            icon={<ArrowLeft />}
            onClick={goBackToMenuEditor}
            disabled={isUploading}
          >
            <FormattedMessage id="shared-menu.editor.back-button" />
          </StyledTextButton>
          <FormattedMessage id="menu.bulk-image.import-item-images" />
        </Header>
        <MenuNameTag icon={<Document />}>{localize(sharedMenu.name, sharedMenu.primary_language)}</MenuNameTag>
      </HeaderContainer>
      <ContentWrapper>
        {loadingData && (
          <SpinnerContainer>
            <Spinner size="large" />
          </SpinnerContainer>
        )}

        {!loadingData && (
          <Content>
            <T.Body3>
              <FormattedMessage id="menu.bulk-image.identifier-selector-label" />
            </T.Body3>
            <RadioGroup>
              <StyledRadio
                checked={matchBy === 'id'}
                onChange={() => setMatchBy('id')}
                disabled={!!matchingResults || isMatching}
              >
                <T.Body3>
                  <FormattedMessage id="menu.bulk-image.identifier-item-id" />
                </T.Body3>
              </StyledRadio>
              <StyledRadio
                checked={matchBy === 'barcode_gtin'}
                onChange={() => setMatchBy('barcode_gtin')}
                disabled={!!matchingResults || isMatching}
              >
                <T.Body3>
                  <FormattedMessage id="menu.bulk-image.identifier-item-gtin" />
                </T.Body3>
              </StyledRadio>
              <StyledRadio
                checked={matchBy === 'merchant_sku'}
                onChange={() => setMatchBy('merchant_sku')}
                disabled={!!matchingResults || isMatching}
              >
                <T.Body3>
                  <FormattedMessage id="menu.bulk-image.identifier-item-sku" />
                </T.Body3>
              </StyledRadio>
            </RadioGroup>
            {(!matchingResults || isMatching) && (
              <Dropzone onDrop={handleFileMatching} useFsAccessApi={false}>
                {({ getRootProps, getInputProps }) => (
                  <FileDrop {...getRootProps()} isLoading={isMatching}>
                    <input {...getInputProps()} />
                    {isMatching ? (
                      <Spinner size="large" />
                    ) : (
                      <div>
                        <FormattedMessage
                          id="menu.bulk-image.choose-or-drop"
                          values={{ action: chunks => <FileSelectLink>{chunks}</FileSelectLink> }}
                        />
                        <FileSpecs>
                          <FormattedMessage id="menu.bulk-image.file-specs" />
                        </FileSpecs>
                      </div>
                    )}
                  </FileDrop>
                )}
              </Dropzone>
            )}

            {!!matchingResults && (
              <>
                {isUploading && (
                  <Alert variant="info">
                    <FormattedMessage
                      id="shared-menu.bulk-image-upload.upload-info"
                      values={{
                        itemsCount: matchingResults.filter(r => r.status === 'success').length,
                        imagesCount: matchingResults.filter(r => r.status === 'success').flatMap(({ files }) => files)
                          .length,
                      }}
                    />
                  </Alert>
                )}
                {uploadStatus === 'success' && (
                  <Alert variant="success">
                    <FormattedMessage id="menu.bulk-image.upload-completed" />
                  </Alert>
                )}
                {uploadStatus === 'idle' && (
                  <Alert variant="success">
                    <FormattedMessage id="menu.bulk-image.match-completed" />
                  </Alert>
                )}
                {uploadStatus === 'error' && (
                  <Alert variant="warning">
                    <FormattedMessage id="menu.bulk-image.upload-errors" />
                  </Alert>
                )}
                {(uploadStatus === 'idle' || isUploading) && (
                  <UploadButtonContainer>
                    <Button
                      variant="primary"
                      size="large"
                      onClick={() => handleBulkImageUpload()}
                      disabled={isUploading || matchingResults.filter(r => r.status === 'success').length === 0}
                      loading={isUploading}
                    >
                      <FormattedMessage id="menu.bulk-image.start-upload" />
                    </Button>
                  </UploadButtonContainer>
                )}
                <StatusTable data={matchingResults} primaryLanguage={sharedMenu.primary_language} />
              </>
            )}
          </Content>
        )}
      </ContentWrapper>
      <FileMatcherConfirmationPrompt when={isPending} onConfirm={() => ({})} />
      <UploadInProgressConfirmationPrompt when={isUploading} />
    </Root>
  );
};
